import { useEffect, useCallback, useState } from 'react'
import MapboxDraw from '@mapbox/mapbox-gl-draw'
// @ts-expect-error - turf types issue
import * as turf from '@turf/turf'
import type { MapRef } from 'react-map-gl'
import type { Feature, Polygon } from 'geojson'
import {
  convertFromGeoJSON,
  convertToGeoJSON,
  ensurePolygonClosed,
} from '../../../utils/mapHelpers'
import { updateZoneLocal } from '@/store/slices/zoneSlice'
import type { AppDispatch } from '@/store'

interface UseDrawSetupParams {
  readonly mapRef: React.MutableRefObject<MapRef | null>
  readonly drawRef: React.MutableRefObject<MapboxDraw | null>
  readonly hideDrawControls: boolean
  readonly drawingMode: 'draw' | 'grid' | 'view'
  readonly onAreaCalculated?: (area: number) => void
  readonly onAreaCalculatingChange?: (isCalculating: boolean) => void
  readonly onDistanceCalculatingChange?: (isCalculating: boolean) => void
  readonly editingZoneId?: string | null
  readonly dispatch: AppDispatch
  readonly onAreaCalculatedExternal?: (area: number) => void
  readonly hasPolygon?: boolean
  readonly onPolygonChange?: (hasPolygon: boolean) => void
}

/**
 * Helper to remove temporary or invalid features
 */
// Functionality moved into the main effect to handle state synchronization better

export const useDrawSetup = ({
  mapRef,
  drawRef,
  hideDrawControls,
  drawingMode,
  onAreaCalculated,
  onAreaCalculatingChange,
  onDistanceCalculatingChange,
  editingZoneId,
  dispatch,
  onAreaCalculatedExternal,
  onPolygonChange,
}: UseDrawSetupParams) => {
  const [isDrawReady, setIsDrawReady] = useState(false)

  // Initialize Mapbox Draw
  const initializeDraw = useCallback(() => {
    const map = mapRef.current?.getMap()
    if (!map) {
      return false
    }
    if (drawRef.current) {
      setIsDrawReady(true)
      return true
    }

    const draw = new MapboxDraw({
      displayControlsDefault: false,
      defaultMode: 'simple_select',
    })
    map.addControl(draw, 'top-left')
    drawRef.current = draw
    setIsDrawReady(true)
    return true
  }, [mapRef, drawRef])

  useEffect(() => {
    const currentMapRef = mapRef.current
    initializeDraw()

    // Polling backup: if map isn't ready immediately, check every 250ms
    const interval = setInterval(() => {
      if (!isDrawReady) {
        const success = initializeDraw()
        if (success) clearInterval(interval)
      } else {
        clearInterval(interval)
      }
    }, 250)

    return () => {
      clearInterval(interval)
      const currentMap = currentMapRef?.getMap()
      const currentDraw = drawRef.current
      if (currentMap && currentDraw && currentMap.hasControl(currentDraw)) {
        currentMap.removeControl(currentDraw)
      }
      drawRef.current = null
      setIsDrawReady(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initializeDraw, mapRef, drawRef])

  // Helper: Handle drawing mode (create mode)
  const handleDrawingMode = useCallback(
    (draw: MapboxDraw) => {
      const hasActualPolygon = draw
        .getAll()
        .features.some(f => f.geometry.type === 'Polygon')

      const currentMode = draw.getMode()
      if (!hasActualPolygon && currentMode !== 'draw_polygon') {
        // eslint-disable-next-line no-console
        console.log('[useDrawSetup] Changing to draw_polygon mode')
        draw.changeMode('draw_polygon')
      }

      // Verify/Enforce mode after a short delay
      setTimeout(() => {
        const currentDraw = drawRef.current
        if (!currentDraw) return

        const hasPoly = currentDraw
          .getAll()
          .features.some(f => f.geometry.type === 'Polygon')
        const mode = currentDraw.getMode()

        if (!hasPoly && mode !== 'draw_polygon') {
          // eslint-disable-next-line no-console
          console.log(
            '[useDrawSetup] Enforcing draw_polygon mode (was ' + mode + ')'
          )
          currentDraw.changeMode('draw_polygon')
        }
      }, 150)
    },
    [drawRef]
  )

  // Helper: Handle cleanup mode (view/grid/hidden modes)
  const handleCleanupMode = useCallback(
    (draw: MapboxDraw) => {
      const all = draw.getAll()
      let deletedAny = false

      all.features.forEach(f => {
        const zId = (f.properties as { zoneId?: string })?.zoneId
        if (!zId && f.id) {
          draw.delete(f.id as string)
          deletedAny = true
        }
      })

      if (deletedAny) {
        onPolygonChange?.(false)
      }

      const currentMode = draw.getMode()
      const shouldResetToSelect = hideDrawControls || drawingMode !== 'draw'
      if (
        shouldResetToSelect &&
        currentMode !== 'simple_select' &&
        currentMode !== 'static'
      ) {
        draw.changeMode('simple_select')
      }
    },
    [hideDrawControls, drawingMode, onPolygonChange]
  )

  // Handle Mode Changes
  useEffect(() => {
    if (!isDrawReady) return

    const draw = drawRef.current
    if (!draw) {
      initializeDraw()
      return
    }

    // If we are editing, stay out of the way!
    if (editingZoneId) return

    // Active Drawing Path (Creation mode)
    if (!hideDrawControls && drawingMode === 'draw') {
      handleDrawingMode(draw)
      return
    }

    // Cleanup Path (view/grid/hidden modes)
    if (hideDrawControls || drawingMode !== 'draw') {
      handleCleanupMode(draw)
    }
  }, [
    initializeDraw,
    hideDrawControls,
    drawingMode,
    editingZoneId,
    drawRef,
    isDrawReady,
    handleDrawingMode,
    handleCleanupMode,
  ])

  const handleDrawCreate = useCallback(
    (e: { features: Feature[] }) => {
      const draw = drawRef.current
      const feature = e.features[0]
      if (!draw || !feature) return

      if (
        feature.geometry.type === 'Polygon' ||
        feature.geometry.type === 'MultiPolygon'
      ) {
        // Enforce single polygon
        const all = draw.getAll()
        all.features.forEach(f => {
          if (f.id !== feature.id && f.id) draw.delete(f.id as string)
        })

        const coordinates = (feature.geometry as Polygon).coordinates[0] ?? []
        if (coordinates.length === 0) return

        let frontendCoords = convertFromGeoJSON(coordinates)
        frontendCoords = ensurePolygonClosed(frontendCoords)

        const geoJsonCoords = convertToGeoJSON(frontendCoords)
        onAreaCalculatingChange?.(true)
        const area = turf.area(turf.polygon([geoJsonCoords]))

        onAreaCalculated?.(area / 1000000)
        onAreaCalculatingChange?.(false)
        onAreaCalculatedExternal?.(area / 1000000)
        onPolygonChange?.(true)
      } else if (feature.geometry.type === 'LineString') {
        onDistanceCalculatingChange?.(true)
        setTimeout(() => {
          onDistanceCalculatingChange?.(false)
        }, 300)
      }
    },
    [
      drawRef,
      onAreaCalculatingChange,
      onAreaCalculated,
      onAreaCalculatedExternal,
      onPolygonChange,
      onDistanceCalculatingChange,
    ]
  )

  const handleDrawUpdate = useCallback(
    (e: { features: Feature[] }) => {
      const feature = e.features[0]
      if (!feature) return

      if (
        feature.geometry.type === 'Polygon' ||
        feature.geometry.type === 'MultiPolygon'
      ) {
        const coordinates = (feature.geometry as Polygon).coordinates[0] ?? []
        if (coordinates.length > 0) {
          onPolygonChange?.(true)
          let frontendCoords = convertFromGeoJSON(coordinates)
          frontendCoords = ensurePolygonClosed(frontendCoords)
          onAreaCalculatingChange?.(true)
          const area = turf.area(
            turf.polygon([convertToGeoJSON(frontendCoords)])
          )
          onAreaCalculated?.(area / 1000000)
          onAreaCalculatingChange?.(false)
        }
      }
    },
    [onPolygonChange, onAreaCalculatingChange, onAreaCalculated]
  )

  const handleDrawDelete = useCallback(
    (e: { features: Feature[] }) => {
      if (e.features.some(f => f.geometry.type === 'Polygon')) {
        onPolygonChange?.(false)
      }
    },
    [onPolygonChange]
  )

  const handleDrawModeChange = useCallback(() => {
    const draw = drawRef.current
    if (!draw) return

    const mode = draw.getMode()
    if (editingZoneId && mode === 'simple_select') {
      try {
        const allDrawFeatures = draw.getAll()
        const editingFeature = allDrawFeatures.features.find(
          f =>
            (f.properties as { zoneId?: string })?.zoneId === editingZoneId ||
            f.id === editingZoneId
        )

        if (editingFeature?.geometry.type === 'Polygon') {
          const coords =
            (editingFeature.geometry as Polygon).coordinates[0] ?? []
          if (coords.length > 0) {
            const frontendCoords = ensurePolygonClosed(
              convertFromGeoJSON(coords)
            )
            const area = turf.area(
              turf.polygon([convertToGeoJSON(frontendCoords)])
            )

            dispatch(
              updateZoneLocal({
                id: editingZoneId,
                updates: {
                  coordinates: frontendCoords,
                  area: Math.round(area),
                },
              })
            )
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error saving zone state on deselect:', error)
      }
    }
  }, [drawRef, editingZoneId, dispatch])

  // Helper: Get create polygon feature
  const getCreatePolygonFeature = useCallback((draw: MapboxDraw) => {
    const allFeatures = draw.getAll()
    return (
      allFeatures.features.find(
        f =>
          f.geometry.type === 'Polygon' &&
          !(f.properties as { zoneId?: string })?.zoneId
      ) ?? allFeatures.features.find(f => f.geometry.type === 'Polygon')
    )
  }, [])

  // Helper: Process polygon coordinates
  const processPolygonCoordinates = useCallback((polyCoords: number[][]) => {
    const validCoords = polyCoords.filter(c => c?.[0] != null && c?.[1] != null)
    if (validCoords.length < 4) {
      // eslint-disable-next-line no-console
      console.debug('Polygon has insufficient coordinates:', validCoords.length)
      return null
    }

    const frontendCoords = ensurePolygonClosed(convertFromGeoJSON(validCoords))
    if (frontendCoords.length < 4) {
      // eslint-disable-next-line no-console
      console.debug(
        'Frontend coords insufficient after conversion:',
        frontendCoords.length
      )
      return null
    }

    try {
      const area = turf.area(turf.polygon([convertToGeoJSON(frontendCoords)]))
      return { coordinates: frontendCoords, area: Math.round(area) }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error processing polygon:', error)
      return null
    }
  }, [])

  // Window helpers setup
  useEffect(() => {
    if (!isDrawReady) return

    const draw = drawRef.current
    if (!draw) return

    window._drawControlGetCreatePolygon = async () => {
      const createPolygonFeature = getCreatePolygonFeature(draw)
      if (
        !createPolygonFeature ||
        createPolygonFeature.geometry.type !== 'Polygon'
      ) {
        return null
      }

      const polyCoords =
        (createPolygonFeature.geometry as Polygon).coordinates[0] ?? []
      return processPolygonCoordinates(polyCoords)
    }

    return () => {
      delete window._drawControlGetCreatePolygon
    }
  }, [drawRef, isDrawReady, getCreatePolygonFeature, processPolygonCoordinates])

  // Separate effect for event listeners
  useEffect(() => {
    if (!isDrawReady) return

    const map = mapRef.current?.getMap()
    const draw = drawRef.current
    if (!map || !draw) return

    map.on('draw.create', handleDrawCreate)
    map.on('draw.update', handleDrawUpdate)
    map.on('draw.delete', handleDrawDelete)
    map.on('draw.modechange', handleDrawModeChange)

    return () => {
      map.off('draw.create', handleDrawCreate)
      map.off('draw.update', handleDrawUpdate)
      map.off('draw.delete', handleDrawDelete)
      map.off('draw.modechange', handleDrawModeChange)
    }
  }, [
    mapRef,
    drawRef,
    handleDrawCreate,
    handleDrawUpdate,
    handleDrawDelete,
    handleDrawModeChange,
    isDrawReady,
  ])

  return { isDrawReady }
}
